import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface CachetFormProps {
  serverErrors?: ServerErrors;
}

export const CachetForm = ({serverErrors}: CachetFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.url"
            label="URL"
            required
            tooltip="Root URL of your Cachet installation."
            autoFocus
          />
        </div>
        <div className="col-6 mb-3">
          <InputField name="settings.token" label="API Token" required tooltip="Your API token." maxLength={20} />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.component"
            label="Component ID"
            required={false}
            tooltip="Component to update with availability status."
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.metric"
            label="Metric ID"
            required={false}
            tooltip="Metric to update with response time data."
          />
        </div>
      </div>
      <SetupInfo metricsWarning={true}>
        <div className="p-2">
          Login to your Cachet status page, then click your avatar in the top left-hand corner. Scroll to the bottom of
          your Profile page and copy the <strong>API Token</strong>. Paste the <strong>API Token</strong> into the API
          Token field above. Return to your Cachet status page, copy the root URL. e.g. (
          <code>https://demo.cachethq.io</code>). Paste the <strong>URL</strong> into the <strong>URL</strong> field
          above.
        </div>
        <div className="p-2">
          To obtain <strong>Component ID</strong>: Click <strong>Component</strong>, look for <strong>API</strong>, then
          click <strong>Edit</strong>. The Component ID is the part of the path before <code>/edit</code>. Paste into
          the <strong>Component ID</strong> field above.
        </div>
        <div className="p-2">
          To obtain <strong>Metric ID</strong>: Click <strong>Metric</strong> and then <strong>Edit</strong> your
          existing metric. The Metric ID is the part of the path before <code>/edit</code>. Paste into the{" "}
          <strong>Metric ID</strong> field above.
        </div>
        <div className="p-2">
          <a href="https://support.uptime.com/hc/en-us/articles/115002536729-Configuring-Cachet" target="_blank">
            View {IntegrationModuleInfo[IntegrationModules.CACHET].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
